import type { FC, ReactNode } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap, styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';

import { useSelectedView } from '../../useSelectedView.ts';

const i18n = defineMessages({
	filterGroupLabel: {
		id: 'home.views.options.filter-group.label',
		defaultMessage: 'Filter results',
		description:
			'Accessible label for the group of filter buttons (such as All, Worked on, Owned by me) that narrow down the list of results shown on the Home page. Only read out by screen readers.',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PillsContainer = styled.div({
	paddingLeft: token('space.300'),
	display: 'flex',
	alignItems: 'center',
	paddingRight: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-button -- To migrate as part of go/ui-styling-standard
const PillButton = styled.button<{
	isSelected?: boolean;
}>({
	outline: 'none',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.isSelected ? token('color.background.selected') : token('color.background.neutral'),
	display: 'inline-flex',
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props.isSelected ? token('color.text.selected') : token('color.text')),
	cursor: 'pointer',
	userSelect: 'none',
	whiteSpace: 'nowrap',
	verticalAlign: 'top',
	marginLeft: token('space.100'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props) =>
			props.isSelected
				? token('color.background.selected.hovered')
				: token('color.background.neutral.hovered'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		marginLeft: 0,
	},

	padding: token('space.100'),
	// TODO: Change B400 here to B700 when it becomes available
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) =>
		props.isSelected
			? `${token('border.width')} solid ${token('color.border.focused')}`
			: `${token('border.width')} solid ${token('color.background.neutral')}`,
	borderRadius: token('radius.full'),
	height: '32px',
	justifyContent: 'center',
	alignItems: 'center',
	minWidth: '48px',
	'&:focus': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		borderColor: 'transparent',
	},
});

const finesseStyles = cssMap({
	selected: {
		borderColor: token('color.border.selected'),
		// Remove the focus ring for pointer interactions while preserving it for keyboard focus.
		'&:focus:not(:focus-visible)': {
			outline: 'none',
			borderColor: token('color.border.selected'),
		},
	},
	unselected: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderColor: token('color.border'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

export type PillFilter = {
	i18n: ReactNode;
	name: string;
};

export type PillProps = {
	pill?: string;
	setPill?: (pill: string) => void;
	pills?: PillFilter[];
};

export const Pills: FC<PillProps> = ({ pill, setPill, pills }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const selectedView = useSelectedView();
	const { formatMessage } = useIntl();

	const onClick = (pillButton: PillFilter) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${selectedView}Screen`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: pillButton.name,
			},
		}).fire();

		setPill && setPill(pillButton.name);
	};

	return pills ? (
		<PillsContainer role="group" aria-label={formatMessage(i18n.filterGroupLabel)}>
			{pills.map((pillButton) => (
				<PillButton
					css={[
						// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
						pillButton.name === pill && fg('platform-dst-tokens-finesse') && finesseStyles.selected,
						pillButton.name !== pill &&
							// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
							fg('platform-dst-tokens-finesse') &&
							finesseStyles.unselected,
					]}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="select-task-view"
					key={pillButton.name}
					isSelected={pillButton.name === pill}
					onClick={() => onClick(pillButton)}
					aria-pressed={pillButton.name === pill}
				>
					{pillButton.i18n}
				</PillButton>
			))}
		</PillsContainer>
	) : null;
};
